import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  BookOpen,
  Building2,
  MessageSquare,
  Plus,
  Send,
  Sparkles,
} from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Kpi, useCrossOrgReport } from "@/features/analytics";
import { useOrgs } from "@/features/orgs";
import { useSystemHealth } from "@/features/system";

/**
 * The Motherhood's front page: every client at a glance, and what is
 * waiting on them.
 *
 * Three questions, in order. Is anything waiting on me (feedback, setups
 * half done)? Is the platform running? How are the clients doing this
 * month? Each answer links to the screen where it is dealt with.
 */
export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminOverview,
});

function AdminOverview() {
  const orgs = useOrgs();
  const report = useCrossOrgReport("month");
  const health = useSystemHealth();

  const list = orgs.data?.items ?? [];
  const waiting = list.filter((o) => (o.openCommentCount ?? 0) > 0);
  const inSetup = list.filter((o) => o.approvalStatus !== "approved");
  const live = list.filter((o) => o.approvalStatus === "approved");
  const verdict = health.data?.verdict;

  return (
    <div>
      <PageHeader
        eyebrow="The Motherhood"
        title="Overview"
        description="Every client, what is waiting on you, and whether the platform is running."
        actions={
          <Button asChild>
            <Link to="/admin/orgs/new">
              <Plus /> New organization
            </Link>
          </Button>
        }
      />

      {orgs.isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              label="Clients"
              value={list.length}
              hint={`${live.length} live, ${inSetup.length} in setup`}
              icon={<Building2 className="size-3.5" />}
            />
            <Kpi
              label="Waiting on you"
              value={waiting.reduce((n, o) => n + (o.openCommentCount ?? 0), 0)}
              hint={
                waiting.length
                  ? `Comments from ${waiting.length} client${waiting.length === 1 ? "" : "s"}`
                  : "No open feedback"
              }
              icon={<MessageSquare className="size-3.5" />}
            />
            <Kpi
              label="Posting this month"
              value={
                report.data
                  ? `${report.data.totals.activePosters} / ${report.data.totals.employees}`
                  : "-"
              }
              hint={
                report.data ? `${report.data.totals.posts} posts across all clients` : undefined
              }
              icon={<Send className="size-3.5" />}
            />
            <Kpi
              label="Platform"
              value={
                verdict === "ok"
                  ? "All good"
                  : verdict === "attention"
                    ? "Needs attention"
                    : verdict === "down"
                      ? "Something is down"
                      : "-"
              }
              hint={
                health.data
                  ? `${health.data.errorsLastDay ?? 0} error${
                      health.data.errorsLastDay === 1 ? "" : "s"
                    } in the last 24h`
                  : "Checking"
              }
              icon={<Activity className="size-3.5" />}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-3">
                <h2 className="font-semibold text-foreground">Needs you</h2>
                {waiting.length === 0 && inSetup.length === 0 && verdict !== "down" ? (
                  <p className="text-muted-foreground text-sm">
                    Nothing is waiting on you. Every client is live and quiet.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {verdict === "down" || verdict === "attention" ? (
                      <Row
                        icon={<Activity className="size-4" />}
                        text={
                          verdict === "down"
                            ? "Something on the platform is down."
                            : "The platform needs a look: a job is late, failing, or behind."
                        }
                        to="/admin/system"
                        label="System health"
                      />
                    ) : null}
                    {waiting.map((o) => (
                      <Row
                        key={o.id}
                        icon={<MessageSquare className="size-4" />}
                        text={`${o.name} sent ${o.openCommentCount} comment${
                          o.openCommentCount === 1 ? "" : "s"
                        } on their brand setup.`}
                        to="/admin/orgs/$orgId"
                        params={{ orgId: o.id }}
                        label="Reply"
                      />
                    ))}
                    {inSetup
                      .filter((o) => (o.openCommentCount ?? 0) === 0)
                      .map((o) => (
                        <Row
                          key={o.id}
                          icon={<Building2 className="size-4" />}
                          text={`${o.name} is not live yet: ${
                            o.approvalStatus === "pending"
                              ? "waiting on their approval"
                              : "brand setup in progress"
                          }.`}
                          to="/admin/orgs/$orgId"
                          params={{ orgId: o.id }}
                          label="Open"
                        />
                      ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3">
                <h2 className="font-semibold text-foreground">Clients</h2>
                {list.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No clients yet. Set up the first one and its brand.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {list.map((o) => (
                      <li key={o.id} className="flex items-center justify-between gap-3 py-2">
                        <Link
                          to="/admin/orgs/$orgId"
                          params={{ orgId: o.id }}
                          className="min-w-0 truncate font-medium text-foreground text-sm hover:underline"
                        >
                          {o.name}
                        </Link>
                        <Badge variant={o.approvalStatus === "approved" ? "success" : "secondary"}>
                          {o.approvalStatus === "approved" ? "Live" : "In setup"}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/admin/templates">
                      <Sparkles /> Brand templates
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/admin/frameworks">
                      <BookOpen /> Frameworks
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  icon,
  text,
  to,
  params,
  label,
}: {
  icon: ReactNode;
  text: string;
  to: "/admin/system" | "/admin/orgs/$orgId";
  params?: { orgId: string };
  label: string;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5">
      <span className="flex min-w-0 items-center gap-2.5 text-foreground text-sm">
        <span className="text-primary">{icon}</span>
        {text}
      </span>
      <Button asChild size="sm" variant="outline">
        <Link to={to} params={params}>
          {label} <ArrowRight />
        </Link>
      </Button>
    </li>
  );
}
